<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Usuários (Adminstrativos)</h1>
      <v-btn color="primary" @click="handleCreateUser">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Usuário
      </v-btn>
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <v-row class="mb-6" dense>
          <v-col cols="12" md="6">
            <v-text-field v-model="searchTerm" placeholder="Pesquisar por nome ou e-mail" clearable
              hide-details prepend-inner-icon="mdi-magnify" @input="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="selectedStatus" :items="statusOptions" placeholder="Filtrar por Status" clearable
              hide-details @update:model-value="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="selectedRole" :items="roleOptions" placeholder="Filtrar por Perfil (Role)" clearable
              hide-details @update:model-value="onFiltersChange" />
          </v-col>
        </v-row>

        <v-data-table-server :headers="columns" :items="users" :items-length="totalItems" :loading="isLoading"
          :items-per-page="itemsPerPage" :page="page" :no-data-text="noDataMessage" class="elevation-0"
          @update:page="page = $event" @update:items-per-page="itemsPerPage = $event">

          <template v-slot:item.status="{ item }">
            <v-chip :color="statusTagMap[item.status]" label>
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.role="{ item }">
            <v-chip color="secondary" label>
              {{ formatRole(item.role) }}
            </v-chip>
          </template>

          <template v-slot:item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon icon="mdi-pencil" color="warning" size="large" class="mr-2" @click="handleEdit(item)"
              title="Editar" />
            <!--<v-icon icon="mdi-delete" color="error" size="large" @click="handleDelete(item)" title="Excluir" />-->
          </template>
        </v-data-table-server>

        <v-alert v-if="hasError && !isLoading" type="error" variant="tonal" class="mt-4 text-center">
          Ocorreu um erro ao carregar os dados dos usuários.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Tem certeza de que deseja excluir o usuário
          <strong>{{ itemToDelete.name }}</strong> (ID: <strong>{{ itemToDelete.id }}</strong>)?
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn color="secondary" variant="flat" @click="deleteDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useToastStore } from '@/store/toast'
import { useLoadingStore } from '@/store/loadingStore'

const router = useRouter()
const toastStore = useToastStore()
const loadingStore = useLoadingStore()
const userData = ref(null)

const users = ref([])
const isLoading = ref(true)
const hasError = ref(false)
const searchTerm = ref('')
const selectedStatus = ref(null)
const selectedRole = ref(null)

const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)

const deleteDialog = ref(false)
const itemToDelete = ref({})

const statusOptions = [
  { title: 'Ativado', value: 'activated' },
  { title: 'Pendente', value: 'pending' },
  { title: 'Cancelado', value: 'canceled' }
]

const roleOptions = [
  { title: 'Administrador', value: 'admin' },
  { title: 'Administrativo', value: 'administrative' }
]

const statusTagMap = {
  activated: 'success',
  pending: 'warning',
  canceled: 'error'
}

const isActiveTagMap = {
  0: 'error',
  1: 'success'
}

const noDataMessage = computed(() => {
  if (isLoading.value) return 'Carregando...'
  return 'Nenhum usuário encontrado.'
})

const columns = [
  { title: 'ID', key: 'id', sortable: true },
  { title: 'Nome', key: 'name', sortable: true },
  { title: 'Email', key: 'email', sortable: true },
  { title: 'Perfil (Role)', key: 'role', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Ativo', key: 'is_active', sortable: true },
  { title: 'Ações', key: 'actions', sortable: false }
]

const loadUsers = async () => {
  isLoading.value = true
  hasError.value = false
  loadingStore.show()
  try {
    const params = {
      page: page.value,
      per_page: itemsPerPage.value,
      search: searchTerm.value, 
      status: selectedStatus.value,
      role: ['admin', 'administrative'],
      email: userData.value.user.email
    }

    const response = await api.get('/users', { params })

    users.value = response.data.data
    totalItems.value = response.data.total
    itemsPerPage.value = response.data.per_page
    page.value = response.data.current_page

  } catch (error) {
    console.error('Erro ao carregar usuários:', error)
    hasError.value = true
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar a lista de usuários.',
      color: 'error'
    })
  } finally {
    isLoading.value = false
    loadingStore.hide()
  }
}

watch([page, itemsPerPage], () => {
  loadUsers()
})

const onFiltersChange = () => {
  page.value = 1
  loadUsers()
}

const handleCreateUser = () => {
  router.push('/app/usuarios/novo') 
}

const handleEdit = async (row) => {
  router.push({ name: 'user-edit', params: { id: row.id } })
}

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = async () => {
  deleteDialog.value = false
  try {
    await api.delete(`/users/${itemToDelete.value.id}`) // Adaptar o endpoint
    toastStore.showToast({
      message: 'Usuário excluído com sucesso!',
      color: 'success'
    })

    await loadUsers()
  } catch (error) {
    console.error('Erro ao excluir usuário:', error)
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao excluir usuário.',
      color: 'error'
    })
  }
}

function formatIsActive(is_active) {
  return is_active === 1 ? 'Sim' : 'Não'
}

function formatStatus(status) {
    if (!status) return 'N/A';
    const statusMap = {
        activated: 'Ativado',
        pending: 'Pendente',
        canceled: 'Cancelado'
    };
    return statusMap[status] || status;
}

function formatRole(role) {
    if (!role) return 'N/A';
    const roleMap = {
        admin: 'Administrador',
        client: 'Cliente',
        administrative: 'Administrativo'
    };
    return roleMap[role] || role;
}

onMounted(() => {
    const storedData = localStorage.getItem("userData")
    if (storedData) userData.value = JSON.parse(storedData)
  loadUsers()
})
</script>

<style scoped>
/* Estilos mantidos do modelo */
</style>